import {
  Box,
  HStack,
  Icon,
  Text,
  VStack,
  chakra,
  Spinner,
  IconButton,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useSelector } from "react-redux";
import { LOADING_STATES } from "../../../../common/constants/common";
import {
  downloadFile,
  isFailed,
  isFinished,
  isInit,
  isLoading,
  isSuccess,
} from "../../../../common/helper/commonHelper";
import DropZoneContentContainer from "../../../../components/DropZoneContentContainer";
import IButton from "../../../../components/IButton";
import IDropzone from "../../../../components/IDropZone";
import IModal from "../../../../components/IModal";
import { useAppDispatch } from "../../../../store";
import {
  checkStatusCsvFile,
  downloadCsvTemplate,
  insertCsvFile,
  processCsvFile,
  resetCsvFileUploadState,
  selectStaticList,
  uploadCsvFile,
} from "../staticlistSlice";

export default function UploadCSVModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const dispatch = useAppDispatch();

  const [file, setFile] = useState<File[]>([]);
  const intervalRef = useRef<NodeJS.Timeout>();
  const { staticListCsv, staticListDetails } = useSelector(selectStaticList);

  function onSelectFile(file: File[]) {
    if (file.length) {
      setFile(file);
      dispatch(resetCsvFileUploadState());
      dispatch(
        uploadCsvFile({
          id: staticListDetails.data?.static_list_id ?? "",
          file: file[0],
        })
      );
    }
  }

  const onCSVClose = useCallback(() => {
    setFile([]);
    onClose();
    dispatch(resetCsvFileUploadState());
  }, [dispatch, onClose]);

  useEffect(() => {
    if (
      file.length &&
      staticListCsv.filepath &&
      isInit(staticListCsv.fileUploadStatus)
    ) {
      const intervalId = setInterval(
        () =>
          dispatch(
            checkStatusCsvFile({
              id: staticListDetails.data?.static_list_id ?? "",
              file: staticListCsv.filepath ?? "",
            })
          ),
        2000
      );
      intervalRef.current = intervalId;
    } else if (
      (!staticListCsv.filepath || isFinished(staticListCsv.fileUploadStatus)) &&
      intervalRef.current
    ) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  }, [
    staticListCsv.filepath,
    staticListCsv.fileUploadStatus,
    dispatch,
    staticListDetails.data,
    file.length,
  ]);

  useEffect(() => {
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, []);

  useEffect(() => {
    if (
      file.length &&
      isSuccess(staticListCsv.fileUploadStatus) &&
      !isFinished(staticListCsv.matching)
    ) {
      dispatch(
        processCsvFile({
          id: staticListDetails.data?.static_list_id ?? "",
          filepath: staticListCsv.filepath ?? "",
        })
      );
    }
  }, [
    staticListCsv.fileUploadStatus,
    file.length,
    staticListCsv.matching,
    staticListCsv.filepath,
    dispatch,
    staticListDetails.data,
  ]);

  useEffect(() => {
    if (isSuccess(staticListCsv.inserting)) onCSVClose();
  }, [staticListCsv.inserting, onCSVClose]);

  function onSubmit() {
    dispatch(
      insertCsvFile({
        id: staticListDetails.data?.static_list_id ?? "",
        filename: staticListCsv.matchDetails?.filename ?? "",
      })
    );
  }

  async function downloadTemplate() {
    const response = await dispatch(downloadCsvTemplate());
    if (response.payload) {
      const template = response.payload as Blob;
      downloadFile(template, "file.csv");
    }
  }

  function FileUploadBar({
    fileName,
    status,
    removeFile,
  }: {
    fileName: string;
    status: LOADING_STATES;
    removeFile: () => void;
  }) {
    function getUploadStatusText(status: LOADING_STATES) {
      if (isLoading(status)) {
        return "Uploading";
      } else if (isSuccess(status)) {
        return "Uploaded";
      } else if (isFailed(status)) {
        return "Error";
      }
    }

    function StatusIcon() {
      return isSuccess(status) ? (
        <Icon color={"brand.green"} as={FaCheck} />
      ) : (
        <Icon color={"brand.red"} as={FaTimes} />
      );
    }

    return (
      <HStack
        justifyContent="space-between"
        bg="gray.100"
        border="1px solid"
        borderColor="gray.200"
        px={3}
        py={1}
        w="60%"
        borderTopRadius="sm"
      >
        <HStack maxW="90%">
          {isFinished(status) ? (
            StatusIcon()
          ) : (
            <Spinner color="brand.blue" size="sm" />
          )}

          <Text color="brand.blue" fontSize="11px">
            {" "}
            {getUploadStatusText(status)}
          </Text>
          <Box
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            maxWidth="65%"
          >
            {fileName}
          </Box>
        </HStack>
        <IconButton
          aria-label="Close"
          variant="solid"
          colorScheme="blackAlpha"
          bg="black"
          name="remove-file"
          size="xs"
          ml="2"
          color="white"
          icon={<FaTimes />}
          borderRadius="50%"
          onClick={removeFile}
        />
      </HStack>
    );
  }

  function FileUploadStatus({
    status,
    message,
    visibility,
  }: {
    status: boolean;
    message: string;
    visibility?: boolean;
  }) {
    return (
      <Box
        height="20px"
        width="60%"
        borderBottomRadius="md"
        px={2}
        bg={status ? "brand.green" : "brand.red"}
        visibility={visibility ? "visible" : "hidden"}
      >
        <Text fontSize="12px" color="white" fontWeight="500">
          {message}
        </Text>
      </Box>
    );
  }

  return (
    <IModal
      header={{ title: "Upload CSV file", capitalize: false }}
      primaryButton={{
        label: "Add to list",
        props: {
          onClick: () => {
            isSuccess(staticListCsv.matching) && onSubmit();
          },
          isDisabled: !isSuccess(staticListCsv.matching),
          isLoading: isLoading(staticListCsv.inserting),
        },
      }}
      secondaryButton={{ label: "Cancel", props: { onClick: onCSVClose } }}
      isOpen={isOpen}
      onClose={onCSVClose}
      size="2xl"
    >
      <VStack position="relative" py={0} overflow="hidden">
        <IButton
          capitalize={false}
          position="absolute"
          fontSize="14px"
          top={0}
          right={0}
          borderRadius="2px"
          name="download-csv-template"
          borderTopRightRadius="md"
          bg="gray.200"
          onClick={downloadTemplate}
          isLoading={staticListCsv.fetchingCsvTemplate}
        >
          Download CSV template
        </IButton>
        <IDropzone onSelectFile={onSelectFile} disabled={!!file.length}>
          <DropZoneContentContainer>
            <Text fontSize="12px" color="gray.500">
              Format should be .csv with only one column named "
              <chakra.span color="brand.blue">email</chakra.span>"
            </Text>
          </DropZoneContentContainer>
        </IDropzone>
        <VStack position="absolute" bottom={0} width="100%" spacing={0}>
          {file.map((item, index) => (
            <FileUploadBar
              fileName={item.name}
              key={index}
              status={staticListCsv.matching}
              removeFile={() => {
                setFile([]);
                dispatch(resetCsvFileUploadState());
              }}
            />
          ))}
          <FileUploadStatus
            status={isSuccess(staticListCsv.matching)}
            message={staticListCsv.matchingMsg}
            visibility={!!file.length && isFinished(staticListCsv.matching)}
          />
        </VStack>
      </VStack>
    </IModal>
  );
}
